import { useState } from 'react';

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  SendOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Button, Card, Collapse } from 'antd';
import { useNavigate, useParams } from 'react-router';

import { useDistributeTestResultsMutation, type ITest } from '@/api/endpoints/schools/schoolSelectionPanel';
import { SurveyResultsModal } from '@/pages/(publicPages)/(schools)/(schoolSelectionPanel)/TestingTab/(pages)/UsersAssignmentPage/_components/SurveyResultsModal/SurveyResultsModal';
import { ConfirmModal, CustomTable } from '@/shared/components';
import { useApiRequest } from '@/shared/hooks';

import { StatCard } from './_components/StatCard/StatCard';
import { ANNOTATION_ITEMS } from './consts';
import { getCollapseItems, getSuccessTestsColumnsData } from './helpers';
import styles from './UsersAssignmentPage.module.scss';

interface IProp {
  passedTestingData: ITest[];
  failedTestingData: ITest[];
}
export const UsersAssignmentPage = ({ passedTestingData, failedTestingData }: IProp) => {
  const [mainListUsersIds, setMainListUsersIds] = useState<Set<number>>(new Set(passedTestingData.map(({ id }) => id)));
  const [isSendTestResultModalOpen, setIsSendTestResultModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<ITest['id'] | null>(null);
  const [isSurveyResultsModalOpen, setIsSurveyResultsModalOpen] = useState(false);

  const isMajorityUsersUnselected =
    passedTestingData.length - mainListUsersIds.size > Math.floor(passedTestingData.length / 2);

  const [distributeTestResults, { isLoading: areTestResultsDistributing }] = useDistributeTestResultsMutation();

  const navigate = useNavigate();
  const { id: schoolId } = useParams();

  const sendApiRequest = useApiRequest();

  const sendTestResultsCb = () => {
    sendApiRequest({
      apiRequestCallback: () =>
        distributeTestResults({ schoolId: Number(schoolId), mainListExamIds: [...mainListUsersIds] }),
      successMessage: 'Списки успешно сформированы',
      successCallback: () => {
        setIsSendTestResultModalOpen(false);
        navigate('..');
      },
    });
  };

  const columns = getSuccessTestsColumnsData({ mainListUsersIds, setMainListUsersIds });
  const collapseItems = getCollapseItems({ failedTestingData });

  const openSurveyResultsModalCb = (id: number) => {
    setSelectedUserId(id);
    setIsSurveyResultsModalOpen(true);
  };

  return (
    <>
      <div className={styles['main-wrapper']}>
        <p className={styles['main-title']}>Формирование списков и отправка уведомлений</p>
        <Card className={styles['annotations-card']}>
          <div className={styles['annotations-wrapper']}>
            <InfoCircleOutlined className={styles['annotations-icon']} />
            <div>
              <p className={styles['annotations-title']}>Как это работает:</p>
              <ul className={styles['annotations-list']}>
                {ANNOTATION_ITEMS.map(({ id, icon, title, description }) => (
                  <li
                    key={id}
                    className={styles['annotations-item']}
                  >
                    {icon}
                    <p className={styles['annotation-info']}>
                      <b className={styles['annotation-title']}>{title}: </b>
                      {description}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
        <div className={styles['stats-wrapper']}>
          <StatCard
            className={styles['stat-card']}
            theme="green"
            icon={<CheckCircleOutlined />}
            title="Основной список"
            value={mainListUsersIds.size}
            steps={['Ожидание документов', 'Модерация']}
          />
          <StatCard
            className={styles['stat-card']}
            theme="yellow"
            icon={<TeamOutlined />}
            title="Резерв"
            value={passedTestingData.length - mainListUsersIds.size}
            steps={['Резерв (без модерации)']}
          />
          <StatCard
            className={styles['stat-card']}
            theme="red"
            icon={<CloseCircleOutlined />}
            title="Тест не сдан"
            value={failedTestingData.length}
            steps={['Уведомление о неуспешном прохождении']}
          />
        </div>
        <Card className={styles['assignment-table-actions-card']}>
          <div>
            <p className={styles['assignment-table-title']}>Участники со статусом "Тест сдан"</p>
            <p className={styles['assignment-table-description']}>
              Распределите участников между основным списком и резервом
            </p>
          </div>
          <Button
            type="primary"
            onClick={() =>
              isMajorityUsersUnselected
                ? setMainListUsersIds(new Set(passedTestingData.map(({ id }) => id)))
                : setMainListUsersIds(new Set())
            }
          >
            {isMajorityUsersUnselected ? 'Выбрать все' : 'Снять все'}
          </Button>
        </Card>
        <CustomTable
          rowKey="id"
          pagination={false}
          dataSource={passedTestingData}
          columns={columns}
          scroll={{ y: 54 * 10 }}
          onRow={({ id }) => ({
            onClick: () => openSurveyResultsModalCb(id),
            style: {
              cursor: 'pointer',
            },
          })}
        />
        <Collapse
          className={styles['test-failed-collapse']}
          items={collapseItems}
          size="large"
          expandIconPlacement="end"
        />
        <div className={styles['buttons-wrapper']}>
          <Button onClick={() => navigate('..')}>Отмена</Button>
          <Button
            color="blue"
            variant="solid"
            icon={<SendOutlined rotate={-45} />}
            onClick={() => setIsSendTestResultModalOpen(true)}
          >
            Отправить уведомления
          </Button>
        </div>
      </div>
      <ConfirmModal
        isModalOpen={isSendTestResultModalOpen}
        handleCancel={() => setIsSendTestResultModalOpen(false)}
        handleConfirm={sendTestResultsCb}
        title="Распределить пользователей"
        description={`Вы уверены, что хотите распределить пользователей и отправить уведомления?
          ${'Важно'.toUpperCase()}! Данное действие можно выполнить только один раз`}
        shouldDisableElement={areTestResultsDistributing}
      />
      {selectedUserId && (
        <SurveyResultsModal
          isModalOpen={isSurveyResultsModalOpen}
          handleCancel={() => setIsSurveyResultsModalOpen(false)}
          schoolId={Number(schoolId)}
          selectedUserId={selectedUserId}
          mainListUsersIds={mainListUsersIds}
          setMainListUsersIds={setMainListUsersIds}
        />
      )}
    </>
  );
};
